import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ButtonField from "../../Ui/ButtonField";
import { getSupportContact } from "../../../config/supportContact";
import { getActiveBrandConfig } from "../../../config/environment.config";
import { useTailwindBrand } from "../../shared";

export const HELP_PAGE_PATH = () => `/${getActiveBrandConfig()}/tableau-de-bord/aide-contact`;

interface HelpCardProps {
    /** Variante compacte (bande dans un tableau de bord) ou carte complète. */
    compact?: boolean;
}

/**
 * Carte « Besoin d'aide ? » des tableaux de bord prof et client : mail et
 * téléphone de l'équipe, et accès au formulaire de contact. Le téléphone
 * n'apparaît que s'il est renseigné dans la configuration de la marque.
 */
const HelpCard: React.FC<HelpCardProps> = ({ compact = false }) => {
    const navigate = useNavigate();
    const { brandConfig } = useTailwindBrand();
    const contact = getSupportContact();
    const primary = brandConfig.colors.primary;
    const ff = brandConfig.fontFamily;

    const lines = (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 3 }} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={1} alignItems="center">
                <MailOutlineRoundedIcon sx={{ fontSize: 18, color: primary }} />
                <Typography
                    component="a"
                    href={`mailto:${contact.email}`}
                    variant="body2"
                    sx={{ fontFamily: ff, color: primary, fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                >
                    {contact.email}
                </Typography>
            </Stack>
            {contact.phone && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneInTalkOutlinedIcon sx={{ fontSize: 18, color: primary }} />
                    <Typography
                        component="a"
                        href={`tel:${contact.phone}`}
                        variant="body2"
                        sx={{ fontFamily: ff, color: primary, fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                    >
                        {contact.phoneDisplay ?? contact.phone}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: ff, color: "text.secondary" }}>
                        ({contact.hours})
                    </Typography>
                </Stack>
            )}
        </Stack>
    );

    return (
        <Box
            sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: `${primary}33`,
                bgcolor: `${primary}08`,
                px: { xs: 2, md: 2.5 },
                py: compact ? 1.5 : 2.5,
            }}
        >
            <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={1.5}
            >
                <Box>
                    <Typography variant={compact ? "subtitle2" : "subtitle1"} fontWeight={700} sx={{ fontFamily: ff, color: primary, lineHeight: 1.2, mb: 0.75 }}>
                        🤝 Besoin d'aide ? Un problème à signaler ?
                    </Typography>
                    {lines}
                </Box>
                <ButtonField
                    label="Nous écrire"
                    variant={compact ? "outlined" : "contained"}
                    size="small"
                    startIcon={<ChatBubbleOutlineRoundedIcon fontSize="small" />}
                    onClick={() => navigate(HELP_PAGE_PATH())}
                />
            </Stack>
        </Box>
    );
};

export default HelpCard;
